import { Injectable, signal } from '@angular/core';

export interface ToastOptions {
  /** How long the toast stays visible, in ms. The prototype uses 2400. */
  duration?: number;
}

export interface ToastState {
  id: number;
  message: string;
}

/**
 * `ToastService` — the global, transient confirmation ("Reservasjonen er
 * avbestilt", "Takk – bekreftet lest"). One toast at a time; a new `show()`
 * replaces the current one and restarts the timer.
 *
 * Render it once per app with `<nb-toast-host>` (typically in the shell, above
 * the tab bar). A toast must NEVER be the only feedback for an action — the
 * screen itself has to reflect the new state too (WCAG 4.1.3 / cognitive load).
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _current = signal<ToastState | null>(null);
  private timer: ReturnType<typeof setTimeout> | undefined;
  private seq = 0;

  /** The toast currently on screen, or `null`. */
  readonly current = this._current.asReadonly();

  show(message: string, options: ToastOptions = {}): void {
    clearTimeout(this.timer);
    const id = ++this.seq;
    this._current.set({ id, message });
    const duration = options.duration ?? 2400;
    this.timer = setTimeout(() => {
      if (this._current()?.id === id) this._current.set(null);
    }, duration);
  }

  dismiss(): void {
    clearTimeout(this.timer);
    this._current.set(null);
  }
}
